import {
  WSElement,
  WSText,
  WSTimeline,
  toWSDateString
} from "@wingspanhq/fe-component-library";
import {
  FinancialActivityEvents,
  Invoice,
  RefundDestinationSummaryType,
  RefundSummary
} from "@wingspanhq/wingspan-api-sdk";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import { TextBulleted } from "../../../shared/components/TextBulleted";

type Props = {
  invoice: Invoice;
};

const refundDestinationLabel = (
  type?: RefundDestinationSummaryType
): string => {
  switch (type) {
    case RefundDestinationSummaryType.EXTERNAL_BANK_ACCOUNT:
      return "Bank account";
    case RefundDestinationSummaryType.PAYMENT_CARD:
      return "Debit card";
    case RefundDestinationSummaryType.INTERNAL_ACCOUNT:
      return "Wingspan Wallet";
    case RefundDestinationSummaryType.TAX_WITHHOLDING:
      return "Tax withholding";
    case RefundDestinationSummaryType.DEDUCTION:
      return "Deduction";
    default:
      return "Other";
  }
};

const getEventDetails = (event: keyof FinancialActivityEvents, date: Date) => {
  switch (event) {
    case "initiatedAt":
      return `Refund started (${toWSDateString(date, "monthDayYear")})`;
    case "completedAt":
      return `Successfully refunded (${toWSDateString(date, "monthDayYear")})`;
    case "failedAt":
      return `Refund failed (${toWSDateString(date, "monthDayYear")})`;
    case "returnedAt":
      return `Refund returned (${toWSDateString(date, "monthDayYear")})`;
    case "cancelledAt":
      return `Refund cancelled (${toWSDateString(date, "monthDayYear")})`;
  }
};

export const InvoiceRefunds: React.FC<Props> = ({ invoice }) => {
  return (
    <WSTimeline
      items={(invoice.refunds || []).map(refund => {
        const events = refundEvents(refund);

        return {
          date: events[0]?.date,
          pills: [
            `${formatMoney(refund.amount ?? 0, invoice.currency || "USD")} refund`
          ],
          content: (
            <>
              <WSElement mb="M">
                <WSText.ParagraphSm color="gray400" mb="XS">
                  Refund method:
                </WSText.ParagraphSm>
                <WSText color="gray600">
                  {refundDestinationLabel(refund.destination?.type)}
                </WSText>
              </WSElement>

              <TextBulleted color="gray600">
                {events.map(event => (
                  <li key={event.event}>
                    <WSText.ParagraphSm>
                      {getEventDetails(event.event, event.date)}
                    </WSText.ParagraphSm>
                  </li>
                ))}
              </TextBulleted>
            </>
          )
        };
      })}
    />
  );
};

const refundEvents = (refund: RefundSummary) => {
  const events = Object.entries(refund.events || {})
    .filter(([, date]) => !!date)
    .map(([key, date]) => ({
      event: key as keyof FinancialActivityEvents,
      date: date as Date
    }));

  events.sort((a, b) => a.date.getTime() - b.date.getTime());

  return events;
};
